import { useEffect, useRef } from "react";

// Subtle terminal-style animated background: faint matrix glyph drift on a
// fixed canvas, plus static gridlines + vignette + noise overlays.
// Respects prefers-reduced-motion (renders a single static frame).
export const TerminalBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = 0;
    let height = 0;
    let columns = 0;
    let drops = [];
    const fontSize = 15;
    const glyphs =
      "01{}[]()<>/*=+-;:$#λ→abcdefABCDEF0123456789const=>let();".split("");

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.floor(width / fontSize);
      drops = new Array(columns)
        .fill(0)
        .map(() => Math.floor((Math.random() * height) / fontSize));
    };

    const drawFrame = () => {
      // Fade previous frame for trailing effect
      ctx.fillStyle = "rgba(7, 10, 8, 0.10)";
      ctx.fillRect(0, 0, width, height);
      ctx.font = `${fontSize}px 'IBM Plex Mono', monospace`;

      for (let i = 0; i < drops.length; i++) {
        const char = glyphs[Math.floor(Math.random() * glyphs.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        // Head glyph brighter, tail dim
        const bright = Math.random() > 0.975;
        ctx.fillStyle = bright
          ? "rgba(205, 251, 142, 0.55)"
          : "rgba(168, 232, 92, 0.15)";
        ctx.fillText(char, x, y);
        if (y > height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i] += 0.5;
      }
    };

    setup();

    if (reduce) {
      // Static single pass
      ctx.fillStyle = "rgba(7,10,8,1)";
      ctx.fillRect(0, 0, width, height);
      return;
    }

    let raf;
    let last = 0;
    const fpsInterval = 1000 / 24;
    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      if (now - last < fpsInterval) return;
      last = now;
      drawFrame();
    };
    raf = requestAnimationFrame(loop);

    const onResize = () => setup();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="terminal-background"
      className="pointer-events-none fixed inset-0 -z-10"
      style={{ background: "var(--lx-canvas)" }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ opacity: 0.5 }}
      />
      {/* Gridlines */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(214,255,226,0.055) 1px, transparent 1px), linear-gradient(to bottom, rgba(214,255,226,0.055) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          opacity: 0.6,
        }}
      />
      {/* Top vignette haze */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 45% at 50% 0%, rgba(168,232,92,0.10), transparent 60%)",
        }}
      />
      {/* Bottom fade to keep footer readable */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/3"
        style={{
          background:
            "linear-gradient(to bottom, transparent, var(--lx-canvas))",
        }}
      />
    </div>
  );
};
